import { Controller } from "@hotwired/stimulus"
import { updateAccreditationPreview } from "../helpers/accreditation_preview"

const eventFields = new Set([
    "id", "profile_id", "commander_position", "commander_bgcolor", "commander_fontcolor",
    "badge_default_background_color", "display_days_format", "recto", "verso"
])
const eventProfileFields = new Set([
    "event_id", "profile_id", "color", "top_color", "font_color", "display_days", "display_zones",
    "custom_bottom_text", "display_days_format", "special_himage_style", "header", "verso"
])

export default class extends Controller {
    static targets = ["form", "iframe", "loader"]

    connect() {
        // Enrollment forms own their preview, including user switching and partial updates.
        this.ownsPreview = !this.formTarget.dataset?.controller?.split(" ").includes("enrollment-form")
        if (!this.ownsPreview) return

        this.disconnected = false
        this.scheduleRefresh ||= this.debounce(this.refresh.bind(this), 300)

        this.formTarget.addEventListener("input", this.scheduleRefresh)
        this.formTarget.addEventListener("change", this.scheduleRefresh)
        this.formTarget.addEventListener("dropzone:file-change", this.scheduleRefresh)

        this.refresh()
    }

    disconnect() {
        if (this.ownsPreview === false) return
        this.disconnected = true
        clearTimeout(this.previewRefreshTimeout)
        this.cancelRequest()
        this.loadingFrame?.removeEventListener("load", this.frameLoaded)
        this.frameLoading = false
        this.formTarget.removeEventListener("input", this.scheduleRefresh)
        this.formTarget.removeEventListener("change", this.scheduleRefresh)
        this.formTarget.removeEventListener("dropzone:file-change", this.scheduleRefresh)
    }

    async refresh() {
        if (this.disconnected || !this.hasFormTarget || !this.hasIframeTarget || this.frameLoading) return

        const url = this.element.dataset.accreditationPreviewUrl
        if (!url) return

        const formData = this.previewFormData()
        const signature = JSON.stringify(Array.from(formData.entries()))
        if (signature === this.pendingSignature) return

        this.cancelRequest()
        if (signature === this.renderedSignature) return

        const requestId = this.requestId
        this.pendingSignature = signature
        this.abortController = new AbortController()
        if (this.hasLoaderTarget) this.loaderTarget.classList.remove("d-none")

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
                    "Accept": "text/html"
                },
                body: formData,
                signal: this.abortController.signal
            })
            if (!response.ok) throw new Error(`Preview failed: ${response.status}`)

            const html = await response.text()
            if (requestId !== this.requestId || this.disconnected) return

            if (!updateAccreditationPreview(this.iframeTarget, html)) {
                this.frameLoading = true
                this.loadingFrame = this.iframeTarget
                this.frameLoaded = () => {
                    this.frameLoading = false
                    if (!this.disconnected) this.refresh()
                }
                this.iframeTarget.addEventListener("load", this.frameLoaded, { once: true })
                this.iframeTarget.srcdoc = html
            }
            this.renderedSignature = signature
        } catch (error) {
            if (error.name !== "AbortError" && requestId === this.requestId && !this.disconnected) {
                console.error("Accreditation preview error:", error)
            }
        } finally {
            if (requestId === this.requestId) {
                this.pendingSignature = null
                this.abortController = null
                if (this.hasLoaderTarget) this.loaderTarget.classList.add("d-none")
            }
        }
    }

    cancelRequest() {
        this.requestId = (this.requestId || 0) + 1
        this.abortController?.abort()
        this.abortController = null
        this.pendingSignature = null
        if (this.hasLoaderTarget) this.loaderTarget.classList.add("d-none")
    }

    previewFormData() {
        const result = new FormData()
        for (const [name, value] of new FormData(this.formTarget).entries()) {
            if (typeof value === "string" && this.previewField(name)) result.append(name, value)
        }
        return result
    }

    previewField(name) {
        if (["authenticity_token", "preview_recto_signed_id", "preview_verso_signed_id"].includes(name)) return true

        const eventField = name.match(/^event\[([^\]]+)\]$/)
        if (eventField && eventFields.has(eventField[1])) return true
        const profileField = name.match(/^event_profile\[([^\]]+)\]$/)
        if (profileField && eventProfileFields.has(profileField[1])) return true

        return /^(event|event_profile)\[zone_ids\]\[\]$/.test(name) ||
            /^(event|event_profile)\[zone_labels\]\[[^\]]+\]$/.test(name) ||
            /^event\[event_days_attributes\]\[[^\]]+\]\[(id|day|color|_destroy)\]$/.test(name)
    }

    debounce(callback, delay) {
        return (...args) => {
            clearTimeout(this.previewRefreshTimeout)
            this.previewRefreshTimeout = setTimeout(() => callback(...args), delay)
        }
    }
}
